import { getContentOptions } from "../../utils/utils";

/**
 * Single source of truth for all explore page sections.
 *
 * Each entry has a `type` field:
 *   "activity"    — the following activity feed (always first)
 *   "collection"  — a tag-filtered set of canonical lists
 *   "pickOfMonth" — editorial pick (commented out; deferred post-v1)
 *
 * Collection sections accept an optional `titleLines` field (default: 1) to control
 * how many lines the card title can wrap to in the carousel. Use 2 for sections with
 * longer titles to avoid truncation, while keeping all cards the same height.
 *
 * Collection sections carry a `tagFilter` in Conjunctive Normal Form (AND-of-ORs):
 *   [ clause, clause, ... ]  where clause = [ [kind, slug], ... ]
 * A list matches if it satisfies every clause; within a clause any pair matches.
 *
 * Example — "best-of AND (21st-century OR all-time)":
 *   [[["kind","best-of"]], [["era","21st-century"],["era","all-time"]]]
 *
 * To add a new section, append an entry here — no router changes needed for collections.
 */
export const EXPLORE_SECTIONS = [
  {
    type: "activity",
    title: "Recent Activity",
    seeMoreSlug: "activity",
  },

  // { type: "pickOfMonth" },  // deferred — out of scope for v1

  {
    type: "collection",
    slug: "recommended-by",
    title: "As Recommended By",
    previewDescription:
      "Personal favourites and recommendations from celebrated authors, directors and tastemakers",
    pageDescription:
      "Some of the best recommendations come from the people whose work we already admire. Explore the favourite books, films, and TV shows of authors, directors, creators, and other tastemakers through tightly curated collections of no more than 20 picks each. Less time working out where to start, more time discovering something great.",
    tagFilter: [[["kind", "recommended-by"]]],
    titleLines: 2,
  },
  {
    type: "collection",
    slug: "critics-favourites",
    title: "Critics' Favourites",
    previewDescription:
      "The definitive top 10 of each year, aggregated from hundreds of critics' annual top 10 lists",
    pageDescription:
      "Every year, critics publish 'top 10' lists ranking their favourite works released that year. The collections on this page are created by aggregating hundreds of these rankings and ordering each title by how frequently it appears across them. The result is a consensus snapshot of the films, books, and TV shows most widely acclaimed by critics each year.",
    tagFilter: [[["kind", "critics-top-10"]]],
    // Order by most recent year first, then movie > tvshow > book within each year
    sortBy: "year_content_type",
  },
  {
    type: "collection",
    slug: "award-winners",
    title: "Award Winners",
    previewDescription:
      "Complete collections of every work that has won a major industry award over the years",
    pageDescription:
      "Every year, major industry awards recognise the standout films, TV shows, books, performances, and creative achievements released across cinema, television, and literature. The collections on this page bring together complete winners lists from the world's most prestigious awards, creating a definitive archive of the works that received the industry's highest honours",
    tagFilter: [[["kind", "award-winner"]]],
    titleLines: 2,
  },
  {
    type: "collection",
    slug: "essential-reference",
    title: "Essential Reference",
    previewDescription:
      "The canonical 'greatest of all time' lists, as curated by online communities and leading publications",
    pageDescription:
      "While a well-curated collection prides itself on selectivity, it is impossible to ignore the enduring ‘best of all time’ lists that have become cultural reference points. This page brings together the most influential rankings, from community-ranked favourites on major platforms to editorial selections from leading publications.",
    // Matches lists tagged "best-of" AND either "21st-century" or "all-time"
    tagFilter: [[["kind", "best-of"]]],
    titleLines: 2,
  },
];

/** Set of valid collection slugs — used by the router to validate URL segments. */
export const COLLECTION_SLUGS = new Set(
  EXPLORE_SECTIONS.filter((s) => s.type === "collection").map((s) => s.slug),
);

/**
 * Set of valid content-type URL slugs (movies|tv|books), derived from
 * getContentOptions so the slugs live in a single place. Used by the router to
 * validate the content-type segment of an /explore URL.
 */
export const CONTENT_TYPES = new Set(
  Object.values(getContentOptions())
    .map((option) => option.slug)
    .filter(Boolean),
);
